import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { requiredWithMessage, requiredDependentLegalName } from 'components/profile-card/profile-card.constant';
import { LocationsService } from 'services/locations/locations.service';
import { MASK_TYPE, NumberMaskConfig, SmartForm, SmartModalService } from '@certemy/ui-components';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { getMergedFormErrors } from 'utils/helpers';
import {
  ORGANIZATION_LOCATION_VALIDATION_MESSAGES,
  replaceLocationOrganizationLabel,
} from 'services/locations/locations.constant';
import _uniq from 'lodash-es/uniq';
import _size from 'lodash-es/size';
import _get from 'lodash-es/get';
import { ProfileCardService } from 'components/profile-card/profile-card.service';
import { forkJoin, NEVER, Observable, of, throwError } from 'rxjs';
import {
  dontContainEqual,
  email as emailValidator,
  phoneLengthValidator,
  required,
  textRangeLength,
  withMessage,
} from '@certemy/common';
import { HELP_MESSAGES, VALIDATION_ERRORS } from 'components/organization-users/invite-user/invite-user.constants';
import { OrgUserProfile } from 'interfaces/profile.interface';
import { ROLE_CODES } from 'constants/role.constants';
import {
  Selectors as LabelSelectors,
  UserProfile,
} from 'features/global-organizations-setting-state/global-organizations-setting.state';
import {
  DEFAULT_LABEL_SETTINGS,
  GlobalOrganizationsLabel,
  OrganizationsLabelType,
} from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import { Store } from '@ngrx/store';
import {
  DELETED_MANAGER_SELECT,
  EDIT_VALIDATION_ERRORS,
  EMAIL_ALREADY_EXISTS_IN_THE_SYSTEM_WARNING,
  EXISTING_ACCOUNT_CONFIRMATION_TEXT,
  EXISTING_ACCOUNT_WARNING,
  EXISTING_ACCOUNT_WARNING_PROF_ADDITIONAL,
  INCORRECT_MANAGER_SELECT,
  IUpdateProfile,
  REMOVE_MANAGER,
  VALID_MANAGER_LOCATIONS,
} from 'components/profile-card/modals/edit-profile/edit-profile.constant';
import {
  IPermissions,
  IPolicies,
  PolicyPermissionsService,
} from 'services/policy-permission/policy-permissions.service';
import { ClientServicesService } from 'services/client-services/client-services.service';
import { UserService } from 'services/user/user.service';
import { PROFILE_TYPE_ID } from 'components/organization-users/users/users.constants';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const defaultMultiSelectParams = {
  withSelectAll: true,
};

export type EDIT_PROFILE_POLICIES_KEYS =
  | 'personalInfoEdit'
  | 'titlesFeatureFlag'
  | 'locationsFeatureFlag'
  | 'editOwnProfile'
  | 'canEditProfile'
  | 'organizationRolesFeatureFlag'
  | 'managersFeatureFlag'
  | 'legalNameFeatureFlag';

const EDIT_PROFILE_POLICIES: IPolicies<EDIT_PROFILE_POLICIES_KEYS> = {
  personalInfoEdit: { name: 'personalInfoEdit', url: 'settings/users/personal_information', action: 'edit' },
  titlesFeatureFlag: { name: 'titlesFeatureFlag', url: 'global/feature_flag/titles', action: 'enable' },
  locationsFeatureFlag: { name: 'locationsFeatureFlag', url: 'global/feature_flag/locations', action: 'enable' },
  editOwnProfile: { name: 'editOwnProfile', url: 'settings/users/own_profile', action: 'edit' },
  organizationRolesFeatureFlag: {
    name: 'organizationRolesFeatureFlag',
    url: 'global/feature_flag/organization_roles',
    action: 'enable',
  },
  managersFeatureFlag: {
    name: 'managersFeatureFlag',
    url: 'global/feature_flag/managers',
    action: 'enable',
  },
  legalNameFeatureFlag: {
    name: 'legalNameFeatureFlag',
    url: 'global/feature_flag/legal_name',
    action: 'enable',
  },
};

const DEFAULT_EDIT_PROFILE_PERMISSIONS: IPermissions<EDIT_PROFILE_POLICIES_KEYS> = {
  personalInfoEdit: false,
  titlesFeatureFlag: false,
  locationsFeatureFlag: false,
  editOwnProfile: false,
  organizationRolesFeatureFlag: false,
  managersFeatureFlag: false,
  legalNameFeatureFlag: false,
};

@UntilDestroy()
@Component({
  selector: 'cm-edit-professional-profile-modal',
  templateUrl: './edit-profile.modal.component.html',
  styleUrls: ['./edit-profile.modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditProfileModalComponent implements OnInit, OnDestroy, AfterViewInit {
  _profile: OrgUserProfile;
  _additionalOffices: SmartForm.FieldOption[];
  form: UntypedFormGroup;
  showErrors: boolean;
  validationMessages: string[];
  roleId: ROLE_CODES;
  multiSelectParams = defaultMultiSelectParams;
  organizationLabels: GlobalOrganizationsLabel = DEFAULT_LABEL_SETTINGS;
  permissions: IPermissions<EDIT_PROFILE_POLICIES_KEYS> = DEFAULT_EDIT_PROFILE_PERMISSIONS;
  expand = false;
  managerPlaceholder: string;
  managerIsDeleted = false;

  readonly ROLE_CODES = ROLE_CODES;
  readonly HELP_MESSAGES = HELP_MESSAGES;
  readonly OrganizationsLabelType = OrganizationsLabelType;
  readonly PHONE_MASK_CONFIG: NumberMaskConfig = {
    maskType: MASK_TYPE.PHONE_NUMBER,
    digitLimit: 10,
    prefix: '',
  };

  @Input() set profile(data: OrgUserProfile) {
    this._profile = data;
    this.roleId = data.role_id;
  }
  @Input() currentProfile: UserProfile;
  @Input() countries: SmartForm.FieldOption[];
  @Input() regions: SmartForm.FieldOption[];
  @Input() offices: SmartForm.FieldOption[];
  @Input() additionalRegions: SmartForm.FieldOption[];
  @Input() set additionalOffices(options: SmartForm.FieldOption[]) {
    this.expand = !!options.length;
    this._additionalOffices = options;
  }
  get additionalOffices(): SmartForm.FieldOption[] {
    return this._additionalOffices;
  }
  @Input() titles: SmartForm.FieldOption[];
  @Input() organizationRoles: SmartForm.FieldOption[];
  @Input() phoneCodeCountries: SmartForm.FieldOption[];
  @Input() defaultPhoneCountryId: number;
  @Input() isGlobalProfile: boolean;
  @Input() highlightId: string;

  @Output() updateProfile = new EventEmitter<OrgUserProfile>();

  profileTypeIdOptions: SmartForm.FieldOption[];
  readonly PROFILE_TYPE_ID = PROFILE_TYPE_ID;
  readonly NO_ORGANIZATION_ROLES_MESSAGE = HELP_MESSAGES.NO_ORGANIZATION_ROLES_MESSAGE;

  constructor(
    private ngbActiveModal: NgbActiveModal,
    private fb: UntypedFormBuilder,
    private locationsService: LocationsService,
    private profileCardService: ProfileCardService,
    private store: Store<any>,
    private cd: ChangeDetectorRef,
    private smartModalService: SmartModalService,
    private policyPermissionsService: PolicyPermissionsService,
    private clientServicesService: ClientServicesService,
    private userService: UserService,
    private router: Router,
  ) {
    this.catchUpdateProfileErrors = this.catchUpdateProfileErrors.bind(this);
  }

  ngOnInit(): void {
    this.store
      .select(LabelSelectors.labels)
      .pipe(untilDestroyed(this))
      .subscribe((labels: GlobalOrganizationsLabel) => {
        this.organizationLabels = labels;
        this.profileTypeIdOptions = this.userService.getProfessionalTypeOptions(labels);
      });

    const getOverrides$ = this.isGlobalProfile
      ? this.clientServicesService.getOverrides(this._profile.organization_id)
      : of({ locations: false, titles: false, organizationRoles: false, managers: false });

    forkJoin([
      getOverrides$,
      this.policyPermissionsService.hasMultiplePermissions(Object.values(EDIT_PROFILE_POLICIES)).pipe(take(1)),
    ]).subscribe(([overrides, permissions]) => {
      this.permissions = {
        ...permissions,
        locationsFeatureFlag: this.isGlobalProfile ? overrides.locations : permissions.locationsFeatureFlag,
        titlesFeatureFlag: this.isGlobalProfile ? overrides.titles : permissions.titlesFeatureFlag,
        organizationRolesFeatureFlag: this.isGlobalProfile
          ? overrides.organizationRoles
          : permissions.organizationRolesFeatureFlag,
        canEditProfile:
          this.currentProfile.profile_id === this._profile.profile_id
            ? permissions.editOwnProfile
            : permissions.personalInfoEdit,
        managersFeatureFlag: this.isGlobalProfile ? overrides.managers : permissions.managersFeatureFlag,
        legalNameFeatureFlag: this.isGlobalProfile || permissions.legalNameFeatureFlag,
      };
      this.form = this.createForm();
      if (!this.permissions.canEditProfile) {
        this.form.disable();
      }

      if (this.additionalOffices) {
        this.additionalOffices = this.additionalOffices.map((office) =>
          this.form.get('office_id') && office.id === this.form.get('office_id').value
            ? { ...office, disabled: true }
            : office,
        );
      }

      this.cd.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    if (this.highlightId) {
      setTimeout(() => {
        const element = document.getElementById(this.highlightId);
        if (!element) {
          return;
        }
        element.classList.add('blink');
        setTimeout(() => {
          element.classList.remove('blink');
        }, 9000);
      }, 500);
    }
  }

  private createForm(): UntypedFormGroup {
    const {
      first_name,
      last_name,
      email,
      title_id,
      locations,
      additionalLocations,
      profile_type_id,
      organizationRole,
      mobileCountryInfo,
      mobilePhoneNumber,
      managerData,
      legalFirstName,
      legalMiddleName,
      legalLastName,
    } = this._profile;
    const additionalRoleControls = {};
    switch (this.roleId) {
      case ROLE_CODES.ORGANIZATION_ADMIN:
        if (this.permissions.titlesFeatureFlag) {
          Object.assign(additionalRoleControls, { title_id: [title_id, requiredWithMessage] });
        }
        if (this.permissions.locationsFeatureFlag) {
          Object.assign(additionalRoleControls, {
            country_ids: [_get(locations, 'countries.ids', null), requiredWithMessage],
            region_ids: [_get(locations, 'regions.ids', null), requiredWithMessage],
            office_ids: [_get(locations, 'offices.ids', null), requiredWithMessage],
          });
        }
        if (this.permissions.managersFeatureFlag) {
          if (managerData) {
            this.managerPlaceholder = `${managerData.firstName} ${managerData.lastName}`;
            this.managerIsDeleted = managerData.isDeleted;
          }
          Object.assign(additionalRoleControls, {
            managerProfileId: managerData ? managerData.profileId : null,
          });
        }
        break;
      case ROLE_CODES.PROFESSIONAL:
        if (this.permissions.locationsFeatureFlag) {
          const isRegionsDisabled = !_get(additionalLocations, 'regions.ids', []).length;
          const isOfficesDisabled = !_get(additionalLocations, 'offices.ids', []).length;
          Object.assign(additionalRoleControls, {
            country_id: [_get(locations, 'countries.ids[0]', null), requiredWithMessage],
            region_id: [_get(locations, 'regions.ids[0]', null), requiredWithMessage],
            office_id: [_get(locations, 'offices.ids[0]', null), requiredWithMessage],
            country_ids: [_get(additionalLocations, 'countries.ids', null)],
            region_ids: [
              { value: _get(additionalLocations, 'regions.ids', null), disabled: isRegionsDisabled },
              this.getLocationValidationError(EDIT_VALIDATION_ERRORS.EMPTY_ADDITIONAL_REGION, required),
            ],
            office_ids: [
              { value: _get(additionalLocations, 'offices.ids', null), disabled: isOfficesDisabled },
              this.getLocationValidationError(EDIT_VALIDATION_ERRORS.EMPTY_ADDITIONAL_OFFICE, required),
            ],
          });
        }
        if (this.permissions.organizationRolesFeatureFlag) {
          Object.assign(additionalRoleControls, {
            organizationRoleId: [
              _get(organizationRole, 'organizationRoleId', null),
              _size(this.organizationRoles)
                ? requiredWithMessage
                : withMessage(HELP_MESSAGES.NO_ORGANIZATION_ROLES_ERROR, required),
            ],
          });
        }
        if (this.permissions.managersFeatureFlag) {
          if (managerData) {
            this.managerPlaceholder = `${managerData.firstName} ${managerData.lastName}`;
            this.managerIsDeleted = managerData.isDeleted;
          }
          Object.assign(additionalRoleControls, {
            managerProfileId: managerData ? managerData.profileId : null,
          });
        }
        if (this.permissions.legalNameFeatureFlag) {
          Object.assign(additionalRoleControls, {
            legalFirstName: this.getLegalFirstNameValidators(legalFirstName, legalLastName, profile_type_id),
            legalMiddleName: [legalMiddleName || null],
            legalLastName: this.getLegalLastNameValidators(legalLastName, legalFirstName, profile_type_id),
          });
        }
        break;
    }
    const formGroupOptions =
      this.roleId === ROLE_CODES.PROFESSIONAL
        ? {
            validator: this.getLocationValidationError(
              EDIT_VALIDATION_ERRORS.EQUAL_ADDITIONAL_OFFICE,
              dontContainEqual('office_ids', 'office_id'),
            ),
          }
        : {};
    return this.fb.group(
      {
        first_name: [
          first_name,
          [
            requiredWithMessage,
            textRangeLength(profile_type_id === PROFILE_TYPE_ID.INDIVIDUAL ? 'First name' : 'Name'),
          ],
        ],
        last_name: [
          last_name || '',
          profile_type_id === PROFILE_TYPE_ID.INDIVIDUAL ? [requiredWithMessage, textRangeLength('Last name')] : [],
        ],
        email: [
          email,
          [requiredWithMessage, textRangeLength('Email'), withMessage(VALIDATION_ERRORS.INVALID_EMAIL, emailValidator)],
        ],
        profile_type_id: [profile_type_id],
        mobileCountryId: [_get(mobileCountryInfo, 'countryId', this.defaultPhoneCountryId || null)],
        mobilePhoneNumber: [
          mobilePhoneNumber,
          withMessage(VALIDATION_ERRORS.INVALID_MOBILE_PHONE_NUMBER, phoneLengthValidator(10)),
        ],
        ...additionalRoleControls,
      },
      { ...formGroupOptions },
    );
  }

  private getLegalFirstNameValidators(
    legalFirstName: string,
    legalLastName: string,
    profile_type_id: number,
  ): [string, ValidatorFn] {
    if (profile_type_id === PROFILE_TYPE_ID.FACILITY) {
      return [legalFirstName || null, requiredWithMessage];
    }
    if (legalLastName) {
      return [legalFirstName || null, requiredDependentLegalName];
    }
    return [legalFirstName || null, null];
  }

  private getLegalLastNameValidators(
    legalLastName: string,
    legalFirstName: string,
    profile_type_id: number,
  ): [string, ValidatorFn] {
    if (legalFirstName && profile_type_id === PROFILE_TYPE_ID.INDIVIDUAL) {
      return [legalLastName || null, requiredDependentLegalName];
    }
    return [legalLastName || null, null];
  }

  private getLocationValidationError(message: string, validator: ValidatorFn): ValidatorFn {
    return withMessage(replaceLocationOrganizationLabel(message, this.organizationLabels), validator);
  }

  ngOnDestroy() {}

  onLegalNameChange(event: string, name: string) {
    if (this.form.get('profile_type_id').value === PROFILE_TYPE_ID.INDIVIDUAL) {
      const control = this.form.get(name);
      if ((event && !control.hasValidator(requiredDependentLegalName)) || !event) {
        const validators = event ? [requiredDependentLegalName] : [];
        control.setValidators(validators);
        control.updateValueAndValidity();
      }
    }
  }

  onCountryChange(countryIds: number[], isAdditional = false): void {
    countryIds = this.getValidIds(countryIds);
    if (this.roleId === ROLE_CODES.ORGANIZATION_ADMIN || isAdditional) {
      this.form.patchValue({
        region_ids: null,
        office_ids: null,
      });
    } else {
      this.form.patchValue({
        region_id: null,
        office_id: null,
      });
    }
    if (!_size(countryIds)) {
      return;
    }
    if (!this.isGlobalProfile) {
      this.locationsService
        .getLocationOrganizationRegions(countryIds, {
          withOffices: true,
        })
        .pipe(take(1))
        .subscribe((regions) => {
          if (isAdditional) {
            this.additionalRegions = regions;
          } else {
            this.regions = regions;
          }
        });
    } else {
      this.clientServicesService
        .getRegions(this._profile.organization_id, countryIds, {
          withOffices: true,
        })
        .pipe(take(1))
        .subscribe((regions) => {
          if (isAdditional) {
            this.additionalRegions = regions;
          } else {
            this.regions = regions;
          }
        });
    }
  }

  onRegionChange(regionIds: number[], isAdditional = false): void {
    regionIds = this.getValidIds(regionIds);
    if (this.roleId === ROLE_CODES.ORGANIZATION_ADMIN || isAdditional) {
      this.form.patchValue({
        office_ids: null,
      });
    } else {
      this.form.patchValue({
        office_id: null,
      });
    }
    if (!_size(regionIds)) {
      return;
    }
    if (!this.isGlobalProfile) {
      this.locationsService
        .getLocationOrganizationOffices(regionIds)
        .pipe(take(1))
        .subscribe((offices) => {
          if (isAdditional) {
            this.additionalOffices = offices.map((office) =>
              office.id === this.form.get('office_id').value ? { ...office, disabled: true } : office,
            );
          } else {
            this.offices = offices;
          }
        });
    } else {
      this.clientServicesService
        .getOffices(this._profile.organization_id, regionIds)
        .pipe(take(1))
        .subscribe((offices) => {
          if (isAdditional) {
            this.additionalOffices = offices.map((office) =>
              office.id === this.form.get('office_id').value ? { ...office, disabled: true } : office,
            );
          } else {
            this.offices = offices;
          }
        });
    }
  }

  onOfficeChange() {
    if (this.form.get('office_id').value) {
      this.additionalOffices = this.additionalOffices.map((office) => {
        const { disabled, ...newOffice } = office;
        return office.id === this.form.get('office_id').value ? { ...office, disabled: true } : newOffice;
      });
      if (this.form.get('office_ids').value) {
        const selectedAdditionalOffices = this.form
          .get('office_ids')
          .value.filter((item) => item !== this.form.get('office_id').value);
        this.form.get('office_ids').patchValue(selectedAdditionalOffices);
      } else {
        this.form.get('office_ids').patchValue([]);
      }
    }
  }

  private getValidIds(Ids: number[]): number[] {
    if (Array.isArray(Ids) && Ids.every((id) => id)) {
      return Ids;
    } else {
      return [];
    }
  }

  onModalClose(): void {
    this.ngbActiveModal.dismiss();
  }

  private setValidationMessages(): void {
    this.validationMessages = _uniq(
      getMergedFormErrors(this.form)
        .map((errors) => Object.values(errors).find((el) => typeof el === 'string'))
        .filter((hasValidationMessage) => !!hasValidationMessage) || [
        ORGANIZATION_LOCATION_VALIDATION_MESSAGES.REQUIRED,
      ],
    );
  }

  onModalConfirm(): void {
    if (this.validateForm()) {
      if (this.managerIsDeleted) {
        this.showWarningForDeletedManager();
        return;
      }

      if (this.checkManagerLocations()) {
        this.profileCardService
          .checkManagerLocations(this._profile.profile_id, { officeIds: this.form.get('office_ids').value })
          .pipe(
            catchError(() => this.showWarningForChangedManagerLocations()),
            take(1),
          )
          .subscribe(() => {
            this.updateProfileData();
          });
        return;
      }

      this.updateProfileData();
    }
  }

  validateForm(): boolean {
    this.showErrors = this.form.invalid;
    this.validationMessages = [];
    if (this.showErrors) {
      this.setValidationMessages();
      return false;
    }
    return true;
  }

  checkManagerLocations(): boolean {
    const prevOffices = this._profile.locations ? this._profile.locations.offices.ids : [];
    const actualOffices = this.form.get('office_ids') ? this.form.get('office_ids').value : [];
    return this._profile.isManager && !prevOffices.every((item) => actualOffices.includes(item));
  }

  showWarningForDeletedManager(): void {
    this.smartModalService
      .openNotification({
        title: 'WARNING',
        body: DELETED_MANAGER_SELECT,
        closeButtonText: 'Ok',
      })
      .subscribe(() => {
        this.form.patchValue({ managerProfileId: null });
        this.managerPlaceholder = '';
        this.managerIsDeleted = false;
      });
  }

  showWarningForChangedManagerLocations(): Observable<never> {
    this.smartModalService
      .openConfirm({
        title: 'WARNING',
        body: replaceLocationOrganizationLabel(VALID_MANAGER_LOCATIONS, this.organizationLabels),
        successButtonText: 'Continue',
      })
      .subscribe((data) => {
        if (data) {
          this.updateProfileData();
        }
      });
    return NEVER;
  }

  updateProfileData(): void {
    this.isGlobalProfile ? this.updateGlobalProfile() : this.updateOrgProfile();
  }

  updateAvatar({ imageConfig }): void {
    this._profile = {
      ...this._profile,
      avatar:
        imageConfig && imageConfig.imageFile
          ? {
              ...this._profile.avatar,
              ...imageConfig,
            }
          : imageConfig,
    };
    this.form.markAsDirty();
  }

  profileTypeIdChange(profileTypeId: PROFILE_TYPE_ID): void {
    if (this.roleId !== ROLE_CODES.PROFESSIONAL) {
      return;
    }
    this.showErrors = false;
    const firstNameField = this.form.get('first_name');
    const lastNameField = this.form.get('last_name');
    if (this.permissions.legalNameFeatureFlag) {
      this.changeLegalNameDependOnTypeId(profileTypeId);
    }
    firstNameField.clearValidators();
    firstNameField.setValidators([
      requiredWithMessage,
      textRangeLength(profileTypeId === PROFILE_TYPE_ID.FACILITY ? 'Name' : 'First name'),
    ]);
    lastNameField.clearValidators();
    if (profileTypeId === PROFILE_TYPE_ID.FACILITY) {
      lastNameField.setValue('');
    } else {
      lastNameField.setValidators([requiredWithMessage, textRangeLength('Last name')]);
    }
    firstNameField.updateValueAndValidity();
    lastNameField.updateValueAndValidity();
  }

  private changeLegalNameDependOnTypeId(profileTypeId: PROFILE_TYPE_ID): void {
    const firstLegalNameField = this.form.get('legalFirstName');
    const middleLegalNameField = this.form.get('legalMiddleName');
    const lastLegalNameField = this.form.get('legalLastName');
    lastLegalNameField.clearValidators();
    if (profileTypeId === PROFILE_TYPE_ID.FACILITY) {
      middleLegalNameField.setValue(null);
      lastLegalNameField.setValue(null);
      firstLegalNameField.setValidators([requiredWithMessage, textRangeLength('Legal name')]);
    } else {
      firstLegalNameField.clearValidators();
      if (firstLegalNameField.value) {
        lastLegalNameField.setValidators([requiredDependentLegalName]);
      }
    }
    middleLegalNameField.updateValueAndValidity();
    lastLegalNameField.updateValueAndValidity();
  }

  private updateGlobalProfile(): void {
    const payload = this.preparePayloadForProfileUpdating();
    payload.organization_id = this._profile.organization_id;
    payload.profile_id = this._profile.profile_id;
    payload.role_id = this._profile.role_id;
    payload.organization_profile_id = this._profile.organization_profile_id;
    payload.mobilePhoneNumber = payload.mobilePhoneNumber ? payload.mobilePhoneNumber.replace(/\D+/g, '') : '';
    if (this.isMultipleOrgsWarningAvailable(payload)) {
      this.smartModalService
        .openConfirm({
          title: 'Attention',
          body: EXISTING_ACCOUNT_CONFIRMATION_TEXT,
          rejectButtonText: 'Cancel',
          successButtonText: 'Continue',
        })
        .pipe(
          filter((value) => value),
          switchMap(() => {
            return this.clientServicesService
              .updateProfile(payload)
              .pipe(take(1), catchError(this.catchUpdateProfileErrors));
          }),
        )
        .subscribe((profile: OrgUserProfile) => this.ngbActiveModal.close(profile));
    } else {
      this.clientServicesService
        .updateProfile(payload)
        .pipe(take(1), catchError(this.catchUpdateProfileErrors))
        .subscribe((profile: OrgUserProfile) => this.ngbActiveModal.close(profile));
    }
  }

  private updateOrgProfile(): void {
    const payload = this.preparePayloadForProfileUpdating();
    this.profileCardService
      .updateProfessionalProfile(this._profile.profile_id, payload)
      .pipe(take(1), catchError(this.catchUpdateProfileErrors))
      .subscribe((profile: OrgUserProfile) => this.ngbActiveModal.close(profile));
  }

  private preparePayloadForProfileUpdating(): Partial<IUpdateProfile> {
    const value = this.form.getRawValue();
    const rolePayload = {};
    if (this.permissions.locationsFeatureFlag) {
      switch (this.roleId) {
        case ROLE_CODES.PROFESSIONAL:
          Object.assign(rolePayload, {
            country_ids: [value.country_id],
            region_ids: [value.region_id],
            office_ids: [value.office_id],
            additionalCountryIds: value.country_ids,
            additionalRegionIds: value.region_ids || [],
            additionalOfficeIds: value.office_ids || [],
          });
      }
    }
    return {
      ...value,
      ...rolePayload,
      mobilePhoneNumber: value.mobilePhoneNumber ? value.mobilePhoneNumber.replace(/\D+/g, '') : '',
      avatar: this._profile.avatar,
    };
  }

  private catchUpdateProfileErrors(err): Observable<never> {
    switch (_get(err, 'body.code_name')) {
      case 'USER_HAS_PROFESSIONALS_IN_DIFFERENT_ORGANIZATIONS':
        let text = EXISTING_ACCOUNT_WARNING;
        if (this.roleId === ROLE_CODES.PROFESSIONAL) {
          text += ` ${EXISTING_ACCOUNT_WARNING_PROF_ADDITIONAL}`;
        }
        this.smartModalService
          .openNotification({
            title: 'Attention',
            body: replaceLocationOrganizationLabel(text, this.organizationLabels),
            closeButtonText: 'Ok',
          })
          .subscribe();
        return NEVER;
      case 'EMAIL_ALREADY_EXISTS_IN_THE_SYSTEM_ERROR':
        this.smartModalService
          .openNotification({
            title: 'Attention',
            body: replaceLocationOrganizationLabel(EMAIL_ALREADY_EXISTS_IN_THE_SYSTEM_WARNING, this.organizationLabels),
            closeButtonText: 'Ok',
          })
          .subscribe();
        return NEVER;
      case 'INCORRECT_MANAGER':
        this.form.get('managerId').setErrors({ incorrectManager: INCORRECT_MANAGER_SELECT });
        this.showErrors = true;
        this.setValidationMessages();
        this.cd.markForCheck();
        return NEVER;
      case 'VALIDATION_ERROR':
        this.smartModalService
          .openNotification({
            title: 'WARNING',
            body: this.setManagerErrorMessage(),
            closeButtonText: 'Ok',
          })
          .subscribe(() => {
            this.form.patchValue({ managerProfileId: null });
            this.managerPlaceholder = '';
          });
        return NEVER;
      default:
        const msg = _get(err, 'body.error_message', '');
        if (msg.match(/email/i)) {
          this.form.get('email').setErrors({ required: msg });
          this.showErrors = true;
          this.setValidationMessages();
          this.cd.markForCheck();
          return NEVER;
        }
    }
    return throwError(err);
  }

  setManagerErrorMessage(): string {
    return replaceLocationOrganizationLabel(
      `The manager for this user – ${this.managerPlaceholder} - no longer is associated with one or more {office}(s) associated with this user. Please selected a different manager for this user. Or, if this user no longer has a manager, please leave the manager selection blank.`,
      this.organizationLabels,
    );
  }

  private isMultipleOrgsWarningAvailable(payload): boolean {
    return (
      this._profile.hasProfilesInOtherOrganizations &&
      (this._profile.first_name !== payload.first_name ||
        this._profile.last_name !== payload.last_name ||
        this._profile.email !== payload.email)
    );
  }

  navigateToOrganizationRolesRoute(event): void {
    event.stopPropagation();
    event.preventDefault();

    this.router
      .navigate(['/organization/settings/organization-settings/organization-roles'])
      .then(() => this.onModalClose());
  }

  setManager(event) {
    this.managerPlaceholder = event;
    if (this.managerIsDeleted) {
      this.managerIsDeleted = false;
    }
  }

  removeManager() {
    this.smartModalService
      .openConfirm({
        title: 'WARNING',
        body: REMOVE_MANAGER,
        successButtonText: 'Ok',
      })
      .pipe(
        take(1),
        filter((confirmed) => confirmed),
      )
      .subscribe(() => {
        this.form.get('managerProfileId').patchValue(null);
        this.form.get('managerProfileId').markAsDirty();
        this.managerPlaceholder = '';
        this.managerIsDeleted = false;
      });
  }
}
